import { signal } from "@preact/signals";
import type { Notification } from "../types";

/**
 * Unread notification count for the bell badge.
 */
export const unreadCount = signal<number>(0);

/**
 * Cached notifications list.
 */
export const notifications = signal<Notification[]>([]);

/**
 * Whether notifications have been loaded at least once.
 */
export const notificationsLoaded = signal<boolean>(false);

/**
 * Whether notifications are currently being fetched.
 */
export const notificationsLoading = signal<boolean>(false);

/**
 * Cursor for pagination (ISO date string).
 */
export const notificationsCursor = signal<string | null>(null);

/**
 * Set the unread count (called after fetching).
 */
export function setUnreadCount(count: number): void {
  unreadCount.value = count;
}

/**
 * Set the notifications list.
 */
export function setNotifications(items: Notification[]): void {
  notifications.value = items;
  notificationsLoaded.value = true;
}

/**
 * Append more notifications (for pagination).
 */
export function appendNotifications(items: Notification[]): void {
  notifications.value = [...notifications.value, ...items];
}

/**
 * Mark a notification as read in the local state.
 */
export function markNotificationRead(notificationId: string): void {
  notifications.value = notifications.value.map((n) =>
    n.id === notificationId ? { ...n, readAt: new Date() } : n,
  );
  // Decrement unread count if it was unread
  const notification = notifications.value.find((n) => n.id === notificationId);
  if (notification && !notification.readAt) {
    unreadCount.value = Math.max(0, unreadCount.value - 1);
  }
}

/**
 * Mark all notifications as read in the local state.
 */
export function markAllNotificationsRead(): void {
  notifications.value = notifications.value.map((n) => ({
    ...n,
    readAt: n.readAt ?? new Date(),
  }));
  unreadCount.value = 0;
}

/**
 * Set the pagination cursor.
 */
export function setNotificationsCursor(cursor: string | null): void {
  notificationsCursor.value = cursor;
}

/**
 * Set loading state.
 */
export function setNotificationsLoading(loading: boolean): void {
  notificationsLoading.value = loading;
}
